import { tokens } from "@fluentui/react-components";
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactElement,
} from "react";
import { useNavigate } from "react-router";

import { useAppRouteParams } from "../../../../AppRoutes";
import {
  Version,
  checkIsCancelable,
  checkIsCopyable,
  checkIsEnableDownloadInput,
  checkIsEnableDownloadResult,
  checkIsPublishedByFileUpload,
  checkIsRemovable,
  useUpdateVersion,
} from "../../../../domain/Version";
import { DialogPhaseType } from "../../helpers/hooks";
import { ReferredFromState } from "../helper";

import { DialogVersionCancel } from "./DialogVersionCancel";
import { DialogVersionDelete } from "./DialogVersionDelete";
import { DialogVersionDuplicate } from "./DialogVersionDuplicate";
import { DialogVersionRemove } from "./DialogVersionRemove";
import { DialogVersionRemoveSome } from "./DialogVersionRemoveSome";

import {
  getFileVersionMultiInputs,
  getFileVersionMultiResults,
  postFileVersionSchemaUpdate,
  postFileVersionViews,
} from "@/api/files";
import {
  postDeleteVersion,
  postDuplicateVersion,
  postRemoveVersion,
} from "@/api/version";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import {
  BulkActionRequest,
  createIdleRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { useAppOrgsAndInvitedOrgs } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { useVersions } from "@/components/InOrganizationProvider/VersionsProvider";
import { useGetReferredFroms } from "@/domain/ReferredFrom";
import { useGetSheets } from "@/domain/Sheet";
import { createDefaultVersionTitle } from "@/helpers/versionTitle";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import {
  Menu,
  MenuDivider,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowReset24Regular,
  CopyAdd24Regular,
  Delete24Regular,
  MoreHorizontal24Filled,
} from "@fluentui/react-icons";
import { DialogBulkUpdate } from "./DialogBulkUpdate";
import { useUpdateCategorySomeAction } from "./DialogVersionUpdateCategorySome";

const useTranslation = translation.use(nsVersion);

const MULTI_DOWNLOAD_LIMIT = 10;

const renderDisabledDescription = (action: string, hint: string) => {
  return (
    <>
      <div>{action}</div>
      <div>{hint}</div>
    </>
  );
};

/**
 * @description 複数の場所で利用されるcontextItemsとダイアログを生成
 */
export const useDialogContextMenuItemsVersion = () => {
  const { t } = useTranslation();
  const { organizations, orgServiceType } = useAppOrgsAndInvitedOrgs();
  const { organizationId, versionId } = useAppRouteParams();
  const organization = organizations.find(
    (org) => org.org_id === organizationId
  );
  const [selectedVersionIds, setSelectedVersionIds] = useState([] as string[]);
  const {
    hasVersionsVersionCategoryActionPermission,
    hasVersionActionPermission,
    hasPermission,
    hasPermissionWithId,
  } = usePermissions();
  const hasVersionsWritePermission = hasPermission("versions", "write");
  const hasOptimizeWritePermission = hasPermission("optimize", "write");
  const hasEvaluateWritePermission = hasPermission("evaluate", "write");

  const { versions } = useVersions();
  const updateVersion = useUpdateVersion();
  const { categoriesDropdown } = useVersionCategories();
  const displayedCategoriesDropdown = categoriesDropdown?.filter(
    (c) =>
      c.value !== "all" &&
      c.value !== "disabled" &&
      (hasPermissionWithId("versions_version_category", "write", c.value) ||
        hasVersionsWritePermission)
  );
  const navigate = useNavigate();
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();

  // バージョン複製用のToast管理
  const { runWithToast: runDuplicateWithToast } = useBulkActionToast({
    inProgress: (count) => t.version.duplicate.inProgress({ count }),
    success: (count) => t.version.duplicate.success({ count }),
    error: (count) => t.version.duplicate.error({ count }),
  });

  // バージョン復元用のToast管理
  const { runWithToast: runRestoreWithToast } = useBulkActionToast({
    inProgress: (count) => t.version.restore.inProgress({ count }),
    success: (count) => t.version.restore.success({ count }),
    error: (count) => t.version.restore.error({ count }),
  });

  const getSheets = useGetSheets();
  const getReferredFroms = useGetReferredFroms();
  const [isIncludingReferencedSheets, setIsIncludingReferencedSheets] =
    useState<ReferredFromState>("waiting");

  // NOTE: 単体削除ダイアログのメッセージ出し分けのため、選択が1件のときのみ
  // 被参照シートの有無をチェックする。
  // 複数削除では件数が多くなるとシート取得コストが高いため行わない。
  const singleSelectedVersionId =
    selectedVersionIds.length === 1 ? selectedVersionIds[0] : null;

  useEffect(() => {
    if (!singleSelectedVersionId) {
      setIsIncludingReferencedSheets("waiting");
      return;
    }
    (async () => {
      try {
        const sheets = await getSheets({ versionId: singleSelectedVersionId });
        const referredFroms = await Promise.all(
          sheets.map((sheet) =>
            getReferredFroms({
              versionId: singleSelectedVersionId,
              sheetId: sheet.id,
            })
          )
        );
        if (referredFroms.some((r) => r.length > 0)) {
          setIsIncludingReferencedSheets("including");
        } else {
          setIsIncludingReferencedSheets("notIncluding");
        }
      } catch (error) {
        showErrorNotification(t.version.referencedSheetsCheckError(), error);
        setIsIncludingReferencedSheets("notIncluding");
      }
    })();

    return () => setIsIncludingReferencedSheets("waiting");
  }, [singleSelectedVersionId]);

  const [deleteDialogPhaseType, setDeleteDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [removeDialogPhaseType, setRemoveDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [cancelDialogPhaseType, setCancelDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [duplicateDialogPhaseType, setDuplicateDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [isRemoveSomeDialogOpen, setIsRemoveSomeDialogOpen] = useState(false);
  const [updateCategoryRequest, setUpdateCategoryRequest] =
    useState<BulkActionRequest>(createIdleRequest);
  const [newCategoryId, setNewCategoryId] = useState<string | null>(null);
  const [schemaUpdateSomeDialogPhaseType, setSchemaUpdateSomeDialogPhaseType] =
    useState<DialogPhaseType>(null);
  const [viewUpdateSomeDialogPhaseType, setViewUpdateSomeDialogPhaseType] =
    useState<DialogPhaseType>(null);

  // カテゴリ更新アクションフック（コンポーネントではなくフックとして直接呼び出す）
  useUpdateCategorySomeAction({
    versions,
    updateCategoryRequest,
    setUpdateCategoryRequest,
    setSelectedVersionIds,
    newCategoryId,
  });

  const isAnySelectedVersionRemovable = useMemo(() => {
    return selectedVersionIds.every((id) => {
      const version = versions.find((v) => v.id === id);
      return checkIsRemovable(version ?? null);
    });
  }, [versions, selectedVersionIds]);

  const isAnySelectedVersionCopyable = useMemo(() => {
    return selectedVersionIds.every((id) => {
      const version = versions.find((v) => v.id === id);
      return checkIsCopyable(version ?? null);
    });
  }, [versions, selectedVersionIds]);

  const handleDuplicateVersions = useCallback(
    async (
      targetVersionIds?: string[],
      options?: {
        titleMap?: Map<string, string>; // versionId -> newTitle
      }
    ) => {
      const idsToProcess = targetVersionIds ?? selectedVersionIds;
      const selectedVersions = versions.filter(({ id }) =>
        idsToProcess.includes(id)
      );

      // 名前の重複を避けるため、生成済みタイトルを蓄積しながら計算
      const accumulatedVersions = [...versions];
      const versionsWithNewTitles = selectedVersions.map((version) => {
        // titleMapが提供されていればそちらを優先
        const newTitle =
          options?.titleMap?.get(version.id) ??
          createDefaultVersionTitle({
            title: version.title,
            versions: accumulatedVersions,
            category: version.category,
          });
        accumulatedVersions.push({
          title: newTitle,
          category: version.category,
        } as Version);
        return {
          id: version.id,
          version,
          newTitle,
        };
      });

      // 複製元の全バージョンに物理削除権限がある場合のみ「元に戻す」を出す
      const hasRemovePermissionForAll = selectedVersions.every((v) =>
        hasVersionActionPermission(v, "delete")
      );

      const { failedIds } = await runDuplicateWithToast(
        versionsWithNewTitles,
        async (item) => {
          if (
            checkIsPublishedByFileUpload(item.version) ||
            !checkIsCopyable(item.version)
          ) {
            throw new Error(
              t.version.duplicate.notCopyableError({
                versionId: item.version.id,
              })
            );
          }
          const result = await postDuplicateVersion({
            organizationId,
            versionId: item.version.id,
            title: item.newTitle,
          });
          return result.new_version_id;
        },
        {
          undoItem: hasRemovePermissionForAll
            ? async (newVersionId) => {
                // 物理削除は論理削除済みのバージョンにのみ実行できるため、先に論理削除する
                await postDeleteVersion({
                  organizationId,
                  versionId: newVersionId,
                });
                await postRemoveVersion({
                  organizationId,
                  versionId: newVersionId,
                });
              }
            : undefined,
        }
      );

      setSelectedVersionIds(failedIds);
    },
    [
      t,
      versions,
      selectedVersionIds,
      runDuplicateWithToast,
      organizationId,
      hasVersionActionPermission,
    ]
  );

  const getContextMenuItemsVersion = useCallback(
    (version: Version): ReactElement => {
      const hasVersionWritePermission = hasVersionActionPermission(
        version,
        "write"
      );
      const hasCancelPermission = (() => {
        if (!hasVersionWritePermission || !version.publishedByOperation)
          return false;

        switch (version.publishedByOperation) {
          case "evaluate":
          case "file_evaluate":
            return hasEvaluateWritePermission;
          case "optimize":
          case "file_optimize":
            return hasOptimizeWritePermission;
          default:
            return false;
        }
      })();
      const hasVersionsVersionCategoryWritePermission =
        hasVersionsVersionCategoryActionPermission(version, "write");

      return (
        <MenuList>
          {renderUpdateCategoryMenu(!hasVersionWritePermission, version)}
          <MenuDivider />
          {organization?.is_sheet_enabled &&
            !checkIsPublishedByFileUpload(version) && (
              <MenuItem
                onClick={() => {
                  navigate(
                    `/organizations/${organizationId}/versions/${version.id}`
                  );
                }}
              >
                {t.version.row.openViewAndSheetList()}
              </MenuItem>
            )}
          <MenuItem
            onClick={() => {
              navigate(
                `/organizations/${organizationId}/versions/${version.id}/version_info`
              );
            }}
          >
            {t.version.row.versionInfo()}
          </MenuItem>
          <MenuItem
            onClick={() => {
              navigate(
                `/organizations/${organizationId}/versions/${version.id}/version_info`
              );
            }}
            disabled={!hasVersionWritePermission}
          >
            {t.version.row.rename()}
          </MenuItem>
          <MenuItem
            onClick={async () => {
              if (!navigator.clipboard || !window.isSecureContext) {
                showErrorNotification(t.version.copyName.error());
                return;
              }

              try {
                await navigator.clipboard.writeText(version.title);
                showSuccessNotification(t.version.copyName.success());
              } catch (error) {
                showErrorNotification(t.version.copyName.error(), error);
              }
            }}
          >
            {t.version.row.copyName()}
          </MenuItem>
          {organization?.is_sheet_enabled && (
            <MenuItem
              onClick={() => {
                setSelectedVersionIds([version.id]);
                setDuplicateDialogPhaseType("confirm");
              }}
              disabled={
                !hasVersionsVersionCategoryWritePermission ||
                checkIsPublishedByFileUpload(version) ||
                !checkIsCopyable(version)
              }
            >
              {t.version.row.duplicate()}
            </MenuItem>
          )}
          {checkIsCancelable(version) && orgServiceType && (
            <MenuItem
              onClick={() => {
                setSelectedVersionIds([version.id]);
                setCancelDialogPhaseType("confirm");
              }}
              disabled={!hasCancelPermission}
              style={{
                color: hasCancelPermission
                  ? tokens.colorPaletteRedForeground1
                  : "",
              }}
            >
              {t.version.cancel.menuItem({
                target: t.version.cancel.target[orgServiceType](),
              })}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setSelectedVersionIds([version.id]);
              setDeleteDialogPhaseType("confirm");
            }}
            disabled={!hasVersionWritePermission}
            style={{
              color: hasVersionWritePermission
                ? tokens.colorPaletteRedForeground1
                : "",
            }}
          >
            {t.version.row.delete()}
          </MenuItem>
        </MenuList>
      );
    },
    [
      t,
      organization,
      hasVersionActionPermission,
      hasVersionsVersionCategoryActionPermission,
      orgServiceType,
      hasOptimizeWritePermission,
      hasEvaluateWritePermission,
      handleDuplicateVersions,
    ]
  );

  // バージョン復元処理（single-select / multi-select共通）
  const handleRestoreVersions = useCallback(
    async (versionIds = selectedVersionIds) => {
      const selectedVersions = versions.filter(({ id }) =>
        versionIds.includes(id)
      );

      const { failedIds } = await runRestoreWithToast(
        selectedVersions,
        async (version) => {
          await updateVersion(
            {
              ...version,
              isDisabled: false,
            },
            {}
          );
        }
      );

      setSelectedVersionIds(failedIds);
    },
    [versions, selectedVersionIds, runRestoreWithToast, updateVersion]
  );

  // 論理削除されたバージョンだけ利用可能
  const getContextMenuItemsDisableVersion = useCallback(
    (version: Version): ReactElement => {
      const hasVersionWritePermission = hasVersionActionPermission(
        version,
        "write"
      );
      const hasVersionDeletePermission = hasVersionActionPermission(
        version,
        "delete"
      );
      const isDisabledRemove =
        !hasVersionDeletePermission || !checkIsRemovable(version);

      const handleRestore = () => {
        setSelectedVersionIds([version.id]);
        handleRestoreVersions([version.id]);
      };

      return (
        <MenuList>
          <MenuItem
            key="k1"
            onClick={handleRestore}
            disabled={!hasVersionWritePermission}
          >
            {t.version.toolbar.restore()}
          </MenuItem>
          <MenuItem
            key="k2"
            onClick={() => {
              setSelectedVersionIds([version.id]);
              setRemoveDialogPhaseType("confirm");
            }}
            disabled={isDisabledRemove}
            style={{
              color: !isDisabledRemove ? tokens.colorPaletteRedForeground1 : "",
            }}
          >
            {t.version.toolbar.remove()}
          </MenuItem>
        </MenuList>
      );
    },
    [t, hasVersionActionPermission, handleRestoreVersions]
  );

  // 単体削除では被参照シートのチェック完了を待ってから開き、メッセージのちらつきをおさえる
  const isDeleteDialogReady =
    selectedVersionIds.length !== 1 ||
    isIncludingReferencedSheets !== "waiting";

  const {
    selectedMultiResultDownloadIds,
    selectedMultiInputDownloadIds,
    isDisableDownloadMultiResults,
    isDisableDownloadMultiInputs,
  } = useMemo(() => {
    // NOTE:結果は最適化/評価の成功か、もしくは入力検証エラー時にダウンロード可能
    const selectedMultiResultDownloadIds = versions
      .filter(
        (v) =>
          selectedVersionIds.includes(v.id) && checkIsEnableDownloadResult(v)
      )
      .map(({ id }) => id);
    // NOTE:ファイル最適化で作成されたバージョンであれば入力をダウンロード可能
    const selectedMultiInputDownloadIds = versions
      .filter(
        (v) =>
          selectedVersionIds.includes(v.id) && checkIsEnableDownloadInput(v)
      )
      .map(({ id }) => id);
    const isDisableDownloadMultiResults =
      selectedMultiResultDownloadIds.length === 0 ||
      selectedMultiResultDownloadIds.length > MULTI_DOWNLOAD_LIMIT;
    const isDisableDownloadMultiInputs =
      selectedMultiInputDownloadIds.length === 0 ||
      selectedMultiInputDownloadIds.length > MULTI_DOWNLOAD_LIMIT;
    return {
      selectedMultiResultDownloadIds,
      selectedMultiInputDownloadIds,
      isDisableDownloadMultiResults,
      isDisableDownloadMultiInputs,
    };
  }, [versions, selectedVersionIds]);

  const runMultiResultsDownload = useCallback(async () => {
    if (isDisableDownloadMultiResults) return;
    try {
      await getFileVersionMultiResults({
        organizationId,
        versionIds: selectedMultiResultDownloadIds,
      });
      showSuccessNotification(t.version.download.multiResultsSuccess());
    } catch (error) {
      showErrorNotification(t.version.download.multiResultsError(), error);
    }
  }, [
    t,
    isDisableDownloadMultiResults,
    selectedMultiResultDownloadIds,
    showSuccessNotification,
    showErrorNotification,
  ]);
  const runMultiInputsDownload = useCallback(async () => {
    if (isDisableDownloadMultiInputs) return;
    try {
      await getFileVersionMultiInputs({
        organizationId,
        versionIds: selectedMultiInputDownloadIds,
      });
      showSuccessNotification(t.version.download.multiInputsSuccess());
    } catch (error) {
      showErrorNotification(t.version.download.multiInputsError(), error);
    }
  }, [
    t,
    isDisableDownloadMultiInputs,
    selectedMultiInputDownloadIds,
    showSuccessNotification,
    showErrorNotification,
  ]);

  const renderDownloadsMenu = useCallback(() => {
    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuItem
            disabled={
              isDisableDownloadMultiInputs && isDisableDownloadMultiResults
            }
          >
            {t.version.download.multiMenuItem()}
          </MenuItem>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              onMouseDown={runMultiInputsDownload}
              disabled={isDisableDownloadMultiInputs}
            >
              {t.version.download.multiInputs({
                count: selectedMultiInputDownloadIds.length,
                max: MULTI_DOWNLOAD_LIMIT,
              })}
            </MenuItem>
            <MenuItem
              onMouseDown={runMultiResultsDownload}
              disabled={isDisableDownloadMultiResults}
            >
              {t.version.download.multiResults({
                count: selectedMultiResultDownloadIds.length,
                max: MULTI_DOWNLOAD_LIMIT,
              })}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  }, [
    t,
    isDisableDownloadMultiInputs,
    isDisableDownloadMultiResults,
    selectedMultiInputDownloadIds,
    selectedMultiResultDownloadIds,
  ]);

  // Full version list from provider for title validation (avoid using sliced versions)
  const allVersionsFromProvider = versions;

  const renderDialogsVersion = useCallback(
    (versions: Version[]) => {
      return (
        <>
          <DialogVersionCancel
            versions={versions}
            selectedVersionIds={selectedVersionIds}
            setSelectedVersionIds={setSelectedVersionIds}
            dialogPhase={cancelDialogPhaseType}
            setDialogPhase={setCancelDialogPhaseType}
          />
          <DialogVersionDuplicate
            versions={versions}
            allVersions={allVersionsFromProvider}
            selectedVersionIds={selectedVersionIds}
            setSelectedVersionIds={setSelectedVersionIds}
            dialogPhase={duplicateDialogPhaseType}
            setDialogPhase={setDuplicateDialogPhaseType}
            handleDuplicateVersions={handleDuplicateVersions}
          />
          {isDeleteDialogReady && (
            <DialogVersionDelete
              versions={versions}
              selectedVersionIds={selectedVersionIds}
              setSelectedVersionIds={setSelectedVersionIds}
              isIncludingReferencedSheets={isIncludingReferencedSheets}
              dialogPhase={deleteDialogPhaseType}
              setDialogPhase={setDeleteDialogPhaseType}
            />
          )}
          <DialogVersionRemove
            versions={versions}
            setSelectedVersionIds={setSelectedVersionIds}
            selectedVersionIds={selectedVersionIds}
            dialogPhase={removeDialogPhaseType}
            setDialogPhase={setRemoveDialogPhaseType}
          />

          <DialogVersionRemoveSome
            versions={versions}
            setSelectedVersionIds={setSelectedVersionIds}
            selectedVersionIds={selectedVersionIds}
            open={isRemoveSomeDialogOpen}
            setOpen={setIsRemoveSomeDialogOpen}
          />
          <DialogBulkUpdate
            versions={versions}
            selectedVersionIds={selectedVersionIds}
            setSelectedVersionIds={setSelectedVersionIds}
            kind="sheetSchema"
            updateFunction={async ({
              organizationId,
              versionId,
              file,
              updateMode,
            }) => {
              const opType = updateMode === "merge" ? "normal" : "dangerous";
              await postFileVersionSchemaUpdate({
                organizationId,
                versionId,
                file,
                opType,
              });
            }}
            dialogPhase={schemaUpdateSomeDialogPhaseType}
            setDialogPhase={setSchemaUpdateSomeDialogPhaseType}
            functionName="postFileVersionSchemaUpdate"
          />
          <DialogBulkUpdate
            versions={versions}
            selectedVersionIds={selectedVersionIds}
            setSelectedVersionIds={setSelectedVersionIds}
            kind="viewSetting"
            updateFunction={async ({
              organizationId,
              versionId,
              file,
              updateMode,
            }) => {
              const viewSettingUpdateSaveType =
                updateMode === "merge" ? "merge" : "replace";
              await postFileVersionViews({
                organizationId,
                versionId,
                file,
                viewSettingUpdateSaveType,
              });
            }}
            dialogPhase={viewUpdateSomeDialogPhaseType}
            setDialogPhase={setViewUpdateSomeDialogPhaseType}
            functionName="postFileVersionViews"
          />
        </>
      );
    },
    [
      allVersionsFromProvider,
      selectedVersionIds,
      deleteDialogPhaseType,
      removeDialogPhaseType,
      duplicateDialogPhaseType,
      isRemoveSomeDialogOpen,
      schemaUpdateSomeDialogPhaseType,
      viewUpdateSomeDialogPhaseType,
      isIncludingReferencedSheets,
      handleDuplicateVersions,
    ]
  );

  // 複数バージョン操作
  const isDuplicateableVersionIds = useMemo(
    () =>
      selectedVersionIds.filter((id) => {
        const v = versions.find(({ id: vid }) => vid === id);
        // NOTE: ファイル最適化で作成されたバージョンは複製不可能
        return v ? !checkIsPublishedByFileUpload(v) : false;
      }),
    [versions, selectedVersionIds]
  );
  // 選択バージョンの全てに対しwrite権限があるか
  const hasWriteToAllSelectedVersions =
    selectedVersionIds.length !== 0 &&
    selectedVersionIds.every((versionId) =>
      hasVersionActionPermission(
        versions.find(({ id }) => id === versionId) ?? null,
        "write"
      )
    );
  // 選択バージョンの全てに対しdelete権限があるか
  const hasDeleteToAllSelectedVersions =
    selectedVersionIds.length !== 0 &&
    selectedVersionIds.every((versionId) =>
      hasVersionActionPermission(
        versions.find(({ id }) => id === versionId) ?? null,
        "delete"
      )
    );
  // versions:writeがなければ複製不可
  const isDisableDuplicateVersions =
    !hasVersionsWritePermission ||
    organization?.is_sheet_enabled !== true ||
    isDuplicateableVersionIds.length === 0;

  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );

  const renderUpdateCategoryMenu = useCallback(
    (disabledCondition: boolean, selectedVersion?: Version) => {
      const selectedCategoryIds = selectedVersion
        ? [selectedVersion.category]
        : versions
            .filter((v) => selectedVersionIds.includes(v.id))
            .map((v) => v.category);

      const uniqueCategoryIds = Array.from(new Set(selectedCategoryIds));

      // 1種類だけカテゴリが選択されている場合は、そのカテゴリを除外対象にする
      const excludedCategoryId =
        uniqueCategoryIds.length === 1 ? uniqueCategoryIds[0] : null;

      const filteredCategories = displayedCategoriesDropdown?.filter(
        (c) => c.value !== excludedCategoryId
      );

      return (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuItem
              disabled={disabledCondition || filteredCategories.length == 0}
            >
              {t.version.updateCategory.menuItem()}
            </MenuItem>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuGroupHeader>
                {t.version.updateCategory.groupHeader()}
              </MenuGroupHeader>
              {filteredCategories?.map((categoryDropdown) => (
                <MenuItem
                  key={categoryDropdown.value}
                  onClick={() => {
                    selectedVersion &&
                      setSelectedVersionIds([selectedVersion.id]);
                    setNewCategoryId(categoryDropdown.value);
                    setUpdateCategoryRequest((current) =>
                      current.phase === "idle"
                        ? {
                            phase: "requested",
                            itemIds: selectedVersion
                              ? [selectedVersion.id]
                              : selectedVersionIds,
                          }
                        : current
                    );
                  }}
                >
                  {categoryDropdown.text}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    },
    [
      t,
      selectedVersionIds,
      displayedCategoriesDropdown,
      hasWriteToAllSelectedVersions,
      versions,
    ]
  );

  const renderVersionsToolBar = useCallback(
    (isSelectedEnabledVersions: boolean) => {
      return (
        <Toolbar>
          {isSelectedEnabledVersions ? (
            <>
              <Tooltip
                content={
                  isDisableDuplicateVersions
                    ? renderDisabledDescription(
                        t.version.toolbar.duplicate(),
                        t.version.toolbar.selectTargetHint()
                      )
                    : t.version.toolbar.duplicate()
                }
                relationship="label"
              >
                <ToolbarButton
                  icon={<CopyAdd24Regular />}
                  onClick={() => handleDuplicateVersions()}
                  disabled={
                    isDisableDuplicateVersions || !isAnySelectedVersionCopyable
                  }
                />
              </Tooltip>
              <Tooltip
                content={
                  hasWriteToAllSelectedVersions
                    ? t.version.toolbar.delete()
                    : renderDisabledDescription(
                        t.version.toolbar.delete(),
                        t.version.toolbar.selectTargetHint()
                      )
                }
                relationship="label"
              >
                <ToolbarButton
                  icon={<Delete24Regular />}
                  onClick={() => setDeleteDialogPhaseType("confirm")}
                  disabled={!hasWriteToAllSelectedVersions}
                />
              </Tooltip>
              <ToolbarDivider />
              <Menu>
                <MenuTrigger>
                  <Tooltip
                    content={
                      selectedVersionIds.length === 0
                        ? renderDisabledDescription(
                            t.version.toolbar.moreActions(),
                            t.version.toolbar.selectTargetHint()
                          )
                        : t.version.toolbar.moreActions()
                    }
                    relationship="label"
                  >
                    <ToolbarButton
                      aria-label="More"
                      icon={<MoreHorizontal24Filled />}
                      disabled={selectedVersionIds.length === 0}
                    />
                  </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {hasVersionsMetaWritePermission && (
                      <>
                        <MenuItem
                          onClick={() =>
                            setViewUpdateSomeDialogPhaseType("confirm")
                          }
                          disabled={!hasVersionsMetaWritePermission}
                        >
                          {t.version.toolbar.updateViewSetting()}
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            setSchemaUpdateSomeDialogPhaseType("confirm")
                          }
                          disabled={
                            !hasWriteToAllSelectedVersions ||
                            !hasVersionsMetaWritePermission
                          }
                        >
                          {t.version.toolbar.updateSheetSetting()}
                        </MenuItem>
                      </>
                    )}
                    {renderUpdateCategoryMenu(
                      !selectedVersionIds.length ||
                        !hasWriteToAllSelectedVersions
                    )}
                    {renderDownloadsMenu()}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </>
          ) : (
            <>
              <Tooltip
                content={
                  hasWriteToAllSelectedVersions
                    ? t.version.toolbar.restore()
                    : renderDisabledDescription(
                        t.version.toolbar.restore(),
                        t.version.toolbar.selectTargetHint()
                      )
                }
                relationship="label"
              >
                <ToolbarButton
                  icon={<ArrowReset24Regular />}
                  onClick={() => handleRestoreVersions()}
                  disabled={!hasWriteToAllSelectedVersions}
                />
              </Tooltip>
              <Tooltip
                content={
                  hasDeleteToAllSelectedVersions
                    ? t.version.toolbar.remove()
                    : renderDisabledDescription(
                        t.version.toolbar.remove(),
                        t.version.toolbar.selectTargetHint()
                      )
                }
                relationship="label"
              >
                <ToolbarButton
                  icon={<Delete24Regular />}
                  onClick={() => {
                    // 単一バージョン選択時はシンプルなダイアログを表示
                    if (selectedVersionIds.length === 1) {
                      setRemoveDialogPhaseType("confirm");
                    } else {
                      setIsRemoveSomeDialogOpen(true);
                    }
                  }}
                  disabled={
                    !hasDeleteToAllSelectedVersions ||
                    !isAnySelectedVersionRemovable
                  }
                />
              </Tooltip>
            </>
          )}
        </Toolbar>
      );
    },
    [
      t,
      isDisableDuplicateVersions,
      hasWriteToAllSelectedVersions,
      hasDeleteToAllSelectedVersions,
      renderUpdateCategoryMenu,
      renderDownloadsMenu,
      selectedVersionIds,
      setSchemaUpdateSomeDialogPhaseType,
      setViewUpdateSomeDialogPhaseType,
      setDeleteDialogPhaseType,
      handleDuplicateVersions,
      handleRestoreVersions,
      isAnySelectedVersionRemovable,
      isAnySelectedVersionCopyable,
      hasVersionsMetaWritePermission,
    ]
  );

  return {
    renderDialogsVersion,
    getContextMenuItemsVersion,
    getContextMenuItemsDisableVersion,
    selectedVersionIds,
    setSelectedVersionIds,
    renderVersionsToolBar,
    handleDuplicateVersions,
    handleRestoreVersions,
    setDeleteDialogPhaseType,
    setRemoveDialogPhaseType,
    duplicateDialogPhaseType,
    setDuplicateDialogPhaseType,
  };
};
